import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Phone, Star } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../components/ui/sheet";
import { useGoogleReviews } from "../hooks/useGoogleReviews";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/jj2smc9a_JPEG-afbeelding-4738-BFE8-D0-0.jpeg";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Aannemer", path: "/aannemer" },
  { name: "Badkamer Renovatie", path: "/badkamer-renovatie" },
  { name: "Stukadoor", path: "/stukadoor" },
  { name: "Schilderwerk", path: "/schilderwerk" },
  { name: "Timmerwerk", path: "/timmerwerk" },
  { name: "Uurtarief", path: "/uurtarief" },
  { name: "Contact", path: "/contact" },
];

function GoogleBadge({ reviews, light, compact, testId }) {
  const hasRating = reviews.rating > 0;
  const rating = hasRating ? reviews.rating.toFixed(1) : "Google";
  return (
    <a
      href={reviews.maps_url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      aria-label={hasRating ? `${rating} sterren op Google, ${reviews.total_reviews} reviews` : "Bekijk ons op Google"}
      className={`flex items-center gap-1.5 rounded-full whitespace-nowrap transition-all hover:scale-105 ${compact ? "px-2 py-1" : "px-3 py-1.5"} ${
        light ? "bg-white/20 backdrop-blur-sm hover:bg-white/30" : "bg-slate-100 hover:bg-slate-200"
      }`}
    >
      <Star className={`${compact ? "w-3 h-3" : "w-4 h-4"} text-[#C5A059] fill-[#C5A059]`} />
      <span className={`text-xs font-bold ${light ? "text-white" : "text-slate-700"}`}>{rating}</span>
      {!compact && (
        <span className={`text-xs ${light ? "text-white/80" : "text-slate-500"}`}>
          {hasRating ? `(${reviews.total_reviews}) Google` : "Reviews"}
        </span>
      )}
    </a>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const reviews = useGoogleReviews();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-md"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between gap-6 h-20">
          <Link to="/" className="flex-shrink-0" data-testid="navbar-logo" aria-label="Cardoso Bouw en Onderhoud - Home">
            <div className="bg-white rounded-full p-2 shadow-sm overflow-hidden flex items-center justify-center" style={{width: '56px', height: '56px'}}>
              <img
                src={LOGO_URL}
                alt="Cardoso Bouw en Onderhoud - Aannemer Breukelen"
                className="h-10 w-10 object-contain"
              />
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-5 2xl:gap-7" aria-label="Hoofdmenu">
            {navLinks.filter((link) => link.path !== "/").map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                aria-current={location.pathname === link.path ? "page" : undefined}
                className={`nav-link whitespace-nowrap text-xs 2xl:text-sm font-medium uppercase tracking-wider ${
                  location.pathname === link.path
                    ? "text-[#005F5F]"
                    : scrolled
                    ? "text-slate-700 hover:text-[#005F5F]"
                    : "text-white hover:text-[#C5A059]"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-4">
            <GoogleBadge reviews={reviews} light={!scrolled} testId="navbar-google-badge" />
            <a
              href="tel:+31685071009"
              data-testid="navbar-phone-btn"
              aria-label="Bel 06 85 07 10 09"
              className={`flex items-center gap-2 text-sm font-medium whitespace-nowrap transition-colors hover:text-[#C5A059] ${
                scrolled ? "text-[#005F5F]" : "text-white"
              }`}
            >
              <Phone className="w-4 h-4" />
              <span className="hidden 2xl:inline">06 85 07 10 09</span>
            </a>
            <Link
              to="/contact"
              data-testid="navbar-cta-btn"
              className="bg-[#C5A059] hover:bg-[#005F5F] text-white font-bold uppercase tracking-wider whitespace-nowrap px-5 2xl:px-6 py-3 text-xs 2xl:text-sm transition-all duration-300 hover:-translate-y-0.5"
            >
              Offerte aanvragen
            </Link>
          </div>

          <div className="xl:hidden flex items-center gap-3">
            <GoogleBadge reviews={reviews} light={!scrolled} compact testId="navbar-google-badge-mobile" />

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-toggle"
                  aria-label="Menu openen"
                  className={`p-2 transition-transform duration-200 ${open ? "rotate-90" : ""} ${scrolled ? "text-[#005F5F]" : "text-white"}`}
                >
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-white p-0">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                    <div className="bg-white rounded-full p-2 shadow-sm overflow-hidden flex items-center justify-center" style={{width: '48px', height: '48px'}}>
                      <img
                        src={LOGO_URL}
                        alt="Cardoso Bouw en Onderhoud"
                        className="h-8 w-8 object-contain"
                      />
                    </div>
                    <GoogleBadge reviews={reviews} testId="navbar-google-badge-sheet" />
                  </div>
                  <nav className="flex flex-col p-6 gap-1" aria-label="Mobiel menu">
                    {navLinks.map((link, index) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        data-testid={`mobile-nav-${link.path.replace("/", "") || "home"}`}
                        className={`py-3 px-4 text-base font-medium uppercase tracking-wider transition-all duration-200 ${
                          location.pathname === link.path
                            ? "text-[#005F5F] bg-slate-50 translate-x-1"
                            : "text-slate-700 hover:text-[#005F5F] hover:bg-slate-50 hover:translate-x-1"
                        }`}
                        style={{ transitionDelay: `${index * 30}ms` }}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </nav>
                  <div className="mt-auto p-6 border-t border-slate-100 space-y-3">
                    <a
                      href="tel:+31685071009"
                      data-testid="mobile-phone-btn"
                      className="flex items-center justify-center gap-2 w-full py-3 border-2 border-[#005F5F] text-[#005F5F] font-bold uppercase tracking-wider text-sm hover:bg-[#005F5F] hover:text-white transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      Bellen
                    </a>
                    <Link
                      to="/contact"
                      data-testid="mobile-cta-btn"
                      className="flex items-center justify-center w-full py-3 bg-[#C5A059] text-white font-bold uppercase tracking-wider text-sm hover:bg-[#005F5F] transition-colors"
                    >
                      Offerte aanvragen
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
